<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import type { ConfigCallFormType, Device, deviceKind } from "../../definitions";
import type { Option } from "@/Components/UI/TcSelect.vue";

import TcIcon from "@/Components/Extend/TcIcon.vue";
import ConfigCallForm from "../ConfigCallForm.vue";

import { hasValue } from "@/Utils/Objects";

export interface SettingsProps {
  audio?: Device;
  video?: Device;
  output?: Device;
  blur?: boolean;
  mirror?: boolean;
  devices: Device[];
  isLoadingDevices: boolean;
}

const props = defineProps<SettingsProps>();
const emit = defineEmits<{
  (event: "updateVideo", value: Device): void;
  (event: "updateAudio", value: Device): void;
  (event: "updateOutput", value: Device): void;
  (event: "updateMirror", value: boolean): void;
  (event: "updateBlur", value: boolean): void;
  (event: "close"): void;
  (event: "updateDevices"): void;
}>();

const { t } = useI18n();

const deviceToOption = (device?: Device): Option<string> | undefined => {
  if (!hasValue(device)) return undefined;

  return {
    label: device.label,
    value: device.deviceId,
  };
};

const getDeviceById = (id: string, kind: deviceKind): Device | undefined => {
  return props.devices.find((device) => device.deviceId === id && device.kind === kind);
};

const videoCallConfig = computed((): ConfigCallFormType => ({
  videoId: deviceToOption(props.video),
  audioId: deviceToOption(props.audio),
  outputId: deviceToOption(props.output),
  mirror: props.mirror,
  blur: !!props.blur,
}));

const updateDevice = (event: "updateVideo" | "updateAudio" | "updateOutput") => (value: Option<string>) => {
  let device: Device | undefined = undefined;

  if (event === "updateVideo") {
    device = getDeviceById(value.value as string, "videoInput");
  } else if (event === "updateAudio") {
    device = getDeviceById(value.value as string, "audioInput");
  } else if (event === "updateOutput") {
    device = getDeviceById(value.value as string, "audioOutput");
  }

  if (!hasValue(device)) return;

  // @ts-expect-error type event is not defined
  emit(event, device);
};

const updateMirror = (value: boolean) => {
  emit("updateMirror", value);
};

const updateBlur = (value: boolean) => {
  emit("updateBlur", value);
};
</script>

<template>
  <div class="settings_form">
    <h2 class="typography-high-l mb-4">{{ t("chat.message.video_call.settings.title") }}</h2>
    <TcIcon class="settings_form__close" name="Cross" @click="emit('close')" />

    <ConfigCallForm
      :isLoadingDevices="props.isLoadingDevices"
      :devices="props.devices"
      :config="videoCallConfig"
      @updateVideo="(o) => updateDevice('updateVideo')(o)"
      @updateAudio="(o) => updateDevice('updateAudio')(o)"
      @updateOutput="(o) => updateDevice('updateOutput')(o)"
      @updateMirror="updateMirror"
      @updateBlur="updateBlur"
      @updateDevices="emit('updateDevices')"
    />
  </div>
</template>

<style scoped lang="scss">
.settings_form {
  position: relative;

  &__close {
    position: absolute;
    top: $space-2;
    right: 0;
    cursor: pointer;
  }
}
</style>
